// Shared domain types. Safe to import from web and mobile.

import type { PaymentTier } from './payment-tiers';

export type Score = { team1: number; team2: number };

export type MatchStage =
  | 'group'
  | 'round_of_32'
  | 'round_of_16'
  | 'quarter'
  | 'semi'
  | 'third_place'
  | 'final';

export type MatchStatus = 'scheduled' | 'live' | 'paused' | 'finished';

export type UserRole = 'user' | 'admin';
export type UserStatus = 'active' | 'disabled';

export interface MatchResult {
  fullTime: Score;
  extraTime: Score | null;
  penalties: Score | null;
}

export interface PredictionPayload {
  fullTime: Score;
  extraTime: Score | null;
  penalties: Score | null;
}

export interface ScoringBreakdown {
  fullTime: number;
  extraTime: number;
  penalties: number;
}

export interface ScoringResult {
  total: number;
  breakdown: ScoringBreakdown;
}

// ---------- DTOs returned by the API ----------

export interface UserPublic {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: UserRole;
  status: UserStatus;
  verifiedEmail: boolean;
  favoriteTeamId: string | null;
  favoriteTeamUpdatedAt: string | null;
  reminderEnabled: boolean;
  timezone: string;
  paymentTier: PaymentTier;
  createdAt: string;
}

export interface TeamPublic {
  id: string;
  name: string;
  fifaCode: string;
  flagUrl: string | null;
  group: string | null;
  confederation: string | null;
  kitColor: string | null;
  /** Pre-tournament FIFA world ranking position (1 = best). The final group
   *  tiebreaker. null when not yet seeded. */
  fifaRanking: number | null;
}

/**
 * A team surfaced in the "team of destiny" spin, alongside the number of
 * OTHER users who already have it as their favorite. The destiny wheel uses
 * `pickedBy` to down-weight already-popular teams so the draw remains biased
 * toward spreading users across the pool, without eliminating any team.
 */
export interface DestinyTeamOption {
  team: TeamPublic;
  pickedBy: number;
}

export interface PlayerPublic {
  id: string;
  teamId: string;
  name: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  jerseyNumber: number | null;
  dob: string | null;
  club: string | null;
  photoUrl: string | null;
}

export interface StadiumPublic {
  id: string;
  name: string;
  country: string;
  city: string | null;
  capacity: number | null;
  constructionYear: number | null;
  fact: string | null;
  imageUrl: string | null;
  matchCount: number;
}

/**
 * One player within a match lineup. `grid` is API-Football's "row:col" notation
 * (row 1 = goalkeeper end, column increments left→right within a line) and
 * drives the on-pitch formation render. `null` for bench players or when the
 * upstream omits a position.
 */
export interface FormationPlayer {
  name: string;
  number: number | null;
  pos: 'GK' | 'DEF' | 'MID' | 'FWD' | null;
  grid: string | null;
  /** API-Football media-CDN photo URL for the player's avatar. null when the
   *  upstream omits a player id. May 404 for players without a photo — clients
   *  should fall back to a jersey/placeholder on image load error. */
  photoUrl: string | null;
}

export interface MatchSideLineup {
  formation: string | null;
  starters: FormationPlayer[];
  subs: FormationPlayer[];
}

export interface MatchLineups {
  team1: MatchSideLineup | null;
  team2: MatchSideLineup | null;
}

/** Type of an in-match event surfaced on the match detail card. */
export type MatchEventType =
  | 'goal'
  | 'penalty'
  | 'missed_penalty'
  | 'own_goal'
  | 'yellow_card'
  | 'red_card'
  | 'substitution';

/**
 * A single in-match event, presentation-ready. `side` tells the client which
 * team column to render it under (team1 = home, team2 = away). Events arrive
 * sorted chronologically (ascending) — clients reverse per side for the
 * newest-first display.
 */
export interface MatchEventPublic {
  minute: number;
  type: MatchEventType;
  side: 'team1' | 'team2';
  /**
   * Primary player. For goals/cards: the scorer or carded player. For a
   * `substitution`: the player going OFF. Null when the upstream omits it.
   */
  player: string | null;
  /**
   * Secondary player. For goals: the assist provider. For a `substitution`:
   * the player coming ON. Null otherwise.
   */
  assist: string | null;
}

export interface MatchPublic {
  id: string;
  matchNumber: number;
  stage: MatchStage;
  group: string | null;
  team1: TeamPublic | null;
  team2: TeamPublic | null;
  team1Placeholder: string | null;
  team2Placeholder: string | null;
  kickoffUtc: string;
  venue: string | null;
  city: string | null;
  /**
   * The curated stadium this match is played at, when the match's venue string
   * could be linked to a Stadium record. Null for unlinked matches (e.g. bad
   * upstream venue data) — fall back to `venue`/`city` for display.
   */
  stadium: { id: string; name: string; city: string | null; country: string } | null;
  status: MatchStatus;
  minute: number | null;
  /** Stoppage-time minutes past the base minute (e.g. 2 for "45+2"); null when
   *  not in added time. */
  addedTime: number | null;
  result: MatchResult | null;
  /**
   * Starting XI + bench with formation and on-pitch grid positions, sourced
   * from API-Football. Only populated on the single-match detail endpoint
   * (omitted from list endpoints to keep payloads small); `undefined` there,
   * and `null`/partial until lineups are published ~20–40 min before kickoff.
   */
  lineups?: MatchLineups | null;
  /** Goals, cards, and subs synced from the live feed, sorted chronologically.
   *  Empty for matches without event data (scheduled, or upstream not yet
   *  publishing). */
  events: MatchEventPublic[];
}

/**
 * Render the live clock for display: "45'" normally, "45+" during stoppage
 * time. The upstream feed only signals that a half is in added time (45+, 90+,
 * 105+, 120+) without a reliable extra-minute count, so we show the base minute
 * with a bare "+" rather than a fabricated "45'+1". Returns null when there's
 * no live minute to show.
 */
export function liveMinuteLabel(
  minute: number | null,
  addedTime: number | null = null,
): string | null {
  if (typeof minute !== 'number') return null;
  return addedTime && addedTime > 0 ? `${minute}+` : `${minute}'`;
}

export function isMatchLive(m: Pick<MatchPublic, 'status'>): boolean {
  return m.status === 'live' || m.status === 'paused';
}

/**
 * Canonicalize an upstream venue string so it can be matched against a curated
 * Stadium name. Collapses tab/newline/repeated whitespace and strips a leading
 * "Report" scraping artifact (e.g. "Report\t Estadio Azteca" → "Estadio Azteca").
 * Returns '' for null/empty input. Used by the stadium seed to link matches.
 */
export function normalizeVenue(venue: string | null | undefined): string {
  if (!venue) return '';
  return venue
    .replace(/\s+/g, ' ')
    .replace(/^report\s+/i, '')
    .trim();
}

// Knockout ties pause between periods (the provider has no ET/pens status, only
// break markers). The label keys off the preserved match minute: a level tie at
// 90' awaits extra time, at 105' it's the ET interval, at 120' it awaits pens.
// Group matches only ever pause at half-time (min < 90), so they read "HALF-TIME".
export function matchPausedLabel(minute: number | null): string {
  const min = minute ?? 0;
  if (min >= 120) return 'AWAITING PENALTIES';
  if (min >= 105) return 'ET HALF-TIME';
  if (min >= 90) return 'AWAITING EXTRA TIME';
  return 'HALF-TIME';
}

// Predictions may only be submitted inside the 24h window leading up to
// kickoff. Two exemptions short-circuit the too_early state:
//   1. The tournament opener (matchNumber === 1) is always open so users can
//      place their first-ever prediction while the app is still brand new.
//   2. Callers that pass `allowEarlyPrediction: true` (used for platinum
//      tier and admins via canPredictEarly) bypass the window entirely;
//      kickoff still gates them.
export const PREDICTION_OPEN_WINDOW_MS = 24 * 60 * 60 * 1000;

export type PredictionWindowState =
  | { state: 'open' }
  | { state: 'locked' }
  | { state: 'too_early'; opensAtMs: number };

export function predictionWindowState(
  match: { matchNumber: number; kickoffUtc: string | Date },
  options: { nowMs?: number; allowEarlyPrediction?: boolean } = {},
): PredictionWindowState {
  const nowMs = options.nowMs ?? Date.now();
  const kickoffMs = new Date(match.kickoffUtc).getTime();
  if (kickoffMs <= nowMs) return { state: 'locked' };
  if (match.matchNumber === 1) return { state: 'open' };
  if (options.allowEarlyPrediction) return { state: 'open' };
  const opensAtMs = kickoffMs - PREDICTION_OPEN_WINDOW_MS;
  if (opensAtMs > nowMs) return { state: 'too_early', opensAtMs };
  return { state: 'open' };
}

export interface PredictionPublic {
  id: string;
  userId: string;
  matchId: string;
  submittedAt: string;
  prediction: PredictionPayload;
  pointsAwarded: number | null;
  pointsBreakdown: ScoringBreakdown | null;
}

export interface PredictionWithUser extends PredictionPublic {
  displayName: string;
  avatarUrl: string | null;
  favoriteTeamFlagUrl: string | null;
  // Effective tier — admin accounts are pre-resolved to 'platinum' server-side.
  paymentTier: PaymentTier;
}

// A user's past prediction with its match embedded. Used by the Standings
// "expand a user to see their past predictions" UI so the client doesn't have
// to make a second request to hydrate match info.
export interface PredictionWithMatch extends PredictionPublic {
  match: MatchPublic;
}

export interface FeatureFlagPublic {
  key: string;
  enabled: boolean;
}

// ---------- groups ----------

export interface GroupDto {
  id: string;
  name: string;
  shortId: string;
  ownerId: string;
  ownerDisplayName: string;
  memberCount: number;
  // Non-zero only for groups owned by the requesting user.
  pendingRequestCount: number;
  createdAt: string;
}

export interface GroupInviteDto {
  id: string;
  groupId: string;
  email: string;
  invitedByDisplayName: string;
  createdAt: string;
  expiresAt: string;
}

export interface InvitePreviewDto {
  groupName: string;
  ownerDisplayName: string;
  email: string;
  expiresAt: string;
}

export interface GroupMemberDto {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  favoriteTeamFlagUrl: string | null;
  isOwner: boolean;
  joinedAt: string;
  // Effective tier — admin accounts are pre-resolved to 'platinum' server-side.
  paymentTier: PaymentTier;
}

export interface GroupJoinRequestDto {
  id: string;
  groupId: string;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  favoriteTeamFlagUrl: string | null;
  email: string;
  createdAt: string;
  paymentTier: PaymentTier;
}

/**
 * Public-link (shortId) preview returned by `GET /api/groups/by-short-id/:shortId`.
 * Unauthed — anyone with the link can see the group's name and member count.
 * `viewerState` is only populated when the request carries a bearer token.
 */
export interface GroupLinkPreviewDto {
  groupId: string;
  name: string;
  shortId: string;
  ownerDisplayName: string;
  memberCount: number;
  viewerState:
    | { kind: 'anonymous' }
    | { kind: 'member' }
    | { kind: 'pending' }
    | { kind: 'none' };
}
